/**
 * Date utility functions for achievement timestamps
 */

// Minimum valid year for achievements (fallback if no game release date)
const FALLBACK_MIN_YEAR = 1960;

/**
 * Check if a date is valid for an achievement unlock
 * @param {Date|string} date - The date to validate
 * @param {Date|string|null} gameReleaseDate - The game's release date (optional)
 * @returns {boolean} - Whether the date is valid
 */
export function isValidUnlockDate(date, gameReleaseDate = null) {
  if (!date) return false;
  
  const checkDate = new Date(date);
  if (isNaN(checkDate.getTime())) return false;
  
  const now = new Date();
  
  // Cannot be in the future
  if (checkDate > now) return false;
  
  // Calculate minimum date
  let minDate;
  if (gameReleaseDate) {
    const releaseDate = new Date(gameReleaseDate);
    if (!isNaN(releaseDate.getTime())) {
      // Allow up to 1 year before release (for early access, beta, etc.)
      minDate = new Date(releaseDate);
      minDate.setFullYear(minDate.getFullYear() - 1);
    }
  }
  
  // Use fallback if no valid release date
  if (!minDate) {
    minDate = new Date(FALLBACK_MIN_YEAR, 0, 1); // January 1, 1960
  }
  
  return checkDate >= minDate;
}

/**
 * Get a sanitized unlock date - returns null if invalid
 * @param {Date|string} date - The date to sanitize
 * @param {Date|string|null} gameReleaseDate - The game's release date (optional)
 * @returns {Date|null} - Valid date or null
 */
export function sanitizeUnlockDate(date, gameReleaseDate = null) {
  if (!isValidUnlockDate(date, gameReleaseDate)) {
    return null;
  }
  return new Date(date);
}

/**
 * Get the minimum valid date for a game's achievements
 * @param {Date|string|null} gameReleaseDate - The game's release date (optional)
 * @returns {Date} - Minimum valid date
 */
export function getMinValidDate(gameReleaseDate = null) {
  if (gameReleaseDate) {
    const releaseDate = new Date(gameReleaseDate);
    if (!isNaN(releaseDate.getTime())) {
      const minDate = new Date(releaseDate);
      minDate.setFullYear(minDate.getFullYear() - 1);
      return minDate;
    }
  }
  return new Date(FALLBACK_MIN_YEAR, 0, 1); // January 1, 1960
}

/**
 * Get today's date in YYYY-MM-DD format (for max date input)
 * @returns {string} - Today's date
 */
export function getTodayString() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get the minimum date string for date inputs
 * @param {Date|string|null} gameReleaseDate - The game's release date (optional)
 * @returns {string} - Minimum date in YYYY-MM-DD format
 */
export function getMinDateString(gameReleaseDate = null) {
  return getMinValidDate(gameReleaseDate).toISOString().split('T')[0];
}

/**
 * Validate and clamp a date to valid range
 * @param {Date|string} date - The date to validate
 * @param {Date|string|null} gameReleaseDate - The game's release date (optional)
 * @returns {Date} - Valid date (clamped to range if necessary)
 */
export function clampToValidRange(date, gameReleaseDate = null) {
  const checkDate = new Date(date);
  if (isNaN(checkDate.getTime())) {
    return new Date(); // Return today if invalid
  }
  
  const now = new Date();
  const minDate = getMinValidDate(gameReleaseDate);
  
  // Clamp to range
  if (checkDate > now) return now;
  if (checkDate < minDate) return minDate;
  
  return checkDate;
}

/**
 * Auto-format a date input value into MM/DD/YYYY as the user types.
 * Non-digits are stripped and slashes are inserted at positions 2 and 4.
 * Matches the input mask used by the achievement unlock dialog so any
 * timestamp UI in the app can share the same behavior.
 */
export function formatDateInput(value) {
  const digits = String(value || "").replace(/\D/g, "");
  let out = "";
  if (digits.length > 0) out = digits.slice(0, 2);
  if (digits.length > 2) out += "/" + digits.slice(2, 4);
  if (digits.length > 4) out += "/" + digits.slice(4, 8);
  return out;
}

/**
 * Auto-format a time input value into HH:MM (24-hour) as the user types.
 * Falls back to sensible splits when the user types without a colon.
 * Matches the input mask used by the achievement unlock dialog.
 */
export function formatTimeInput(value) {
  const cleaned = String(value || "").replace(/[^\d:]/g, "");

  if (cleaned.includes(":")) {
    const parts = cleaned.split(":");
    const hours = parts[0].slice(0, 2);
    const minutes = (parts[1] || "").slice(0, 2);
    return minutes ? hours + ":" + minutes : hours;
  }

  const digits = cleaned.replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.length <= 2) return digits;

  if (digits.length === 3) {
    const firstTwo = parseInt(digits.slice(0, 2), 10);
    if (firstTwo <= 23) return digits.slice(0, 2) + ":" + digits[2];
    return digits[0] + ":" + digits.slice(1, 3);
  }

  return digits.slice(0, 2) + ":" + digits.slice(2, 4);
}

